import { useCallback, useEffect, useLayoutEffect, useState, type RefObject } from 'react';

// useLayoutEffect warns during SSR; fall back to useEffect on the server.
const useIsomorphicLayoutEffect = typeof document !== 'undefined' ? useLayoutEffect : useEffect;

export type AnchoredPlacement = 'bottom-end' | 'right-start';

export interface AnchoredCoords {
  top: number;
  left: number;
  maxHeight: number;
}

interface UseAnchoredPositionOptions {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  floatingRef: RefObject<HTMLElement | null>;
  placement?: AnchoredPlacement;
  /** Gap between the anchor and the floating element (px). */
  gap?: number;
  /** Minimum distance kept from the viewport edges (px). */
  margin?: number;
  /** Floating width hint, used for horizontal alignment before the first measure (px). */
  width?: number;
}

/**
 * Position a portaled, `position: fixed` floating element (menu/popover) anchored to a
 * trigger so it is NEVER clipped by the viewport. It measures the floating element's
 * REAL intrinsic height (`scrollHeight` — varies with permissions/content, so a
 * hardcoded estimate is always wrong), picks a placement from the ACTUAL available
 * space (flip up for vertical menus, flip to the left for horizontal popovers), clamps
 * the position into the viewport, and returns a `maxHeight` so the caller can cap with
 * `overflowY: 'auto'` when the element is taller than the space on either side.
 *
 * Returns `null` until measured — render the floating element with `visibility: hidden`
 * (NOT `display: none`, so it stays measurable) until coords arrive, to avoid a flash.
 */
export function useAnchoredPosition({
  open,
  anchorRef,
  floatingRef,
  placement = 'bottom-end',
  gap = 4,
  margin = 8,
  width,
}: UseAnchoredPositionOptions): AnchoredCoords | null {
  const [coords, setCoords] = useState<AnchoredCoords | null>(null);

  const reposition = useCallback(() => {
    const anchor = anchorRef.current;
    const floating = floatingRef.current;
    if (!anchor || !floating) return;

    const a = anchor.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    // scrollHeight = full intrinsic content height, even if a prior maxHeight capped it.
    const fh = Math.max(floating.scrollHeight, Math.round(floating.getBoundingClientRect().height));
    const fw = width ?? Math.round(floating.getBoundingClientRect().width) || 220;

    let top: number;
    let left: number;
    let maxHeight: number;

    if (placement === 'right-start') {
      // Popover to the right of the anchor; flip to the left if no room; clamp vertically.
      left = a.right + gap;
      if (left + fw + margin > vw) left = Math.max(margin, a.left - gap - fw);
      maxHeight = Math.max(0, vh - 2 * margin);
      const drawn = Math.min(fh, maxHeight);
      top = Math.min(Math.max(a.top, margin), Math.max(margin, vh - drawn - margin));
    } else {
      // bottom-end: below the anchor, right-aligned; flip up / scroll if it doesn't fit.
      left = a.right - fw;
      left = Math.min(Math.max(left, margin), Math.max(margin, vw - fw - margin));
      const spaceBelow = vh - a.bottom - margin;
      const spaceAbove = a.top - margin;
      let placeBelow: boolean;
      if (fh + gap <= spaceBelow) {
        placeBelow = true;
        maxHeight = spaceBelow - gap;
      } else if (fh + gap <= spaceAbove) {
        placeBelow = false;
        maxHeight = spaceAbove - gap;
      } else {
        placeBelow = spaceBelow >= spaceAbove;
        maxHeight = Math.max(0, (placeBelow ? spaceBelow : spaceAbove) - gap);
      }
      const drawn = Math.min(fh, maxHeight);
      top = placeBelow ? a.bottom + gap : a.top - gap - drawn;
      top = Math.min(Math.max(top, margin), Math.max(margin, vh - drawn - margin));
    }

    maxHeight = Math.max(0, Math.min(maxHeight, vh - 2 * margin));
    setCoords((prev) =>
      prev && prev.top === top && prev.left === left && prev.maxHeight === maxHeight
        ? prev
        : { top, left, maxHeight },
    );
  }, [anchorRef, floatingRef, placement, gap, margin, width]);

  useIsomorphicLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    reposition();
    const onChange = () => reposition();
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined' && floatingRef.current) {
      ro = new ResizeObserver(() => reposition());
      ro.observe(floatingRef.current);
    }
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
      ro?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reposition]);

  return coords;
}
